import * as THREE from 'three';
import { Entity, bossHpScale, type EntityCtx } from '../Entity';
import { Bullet } from './Bullet';
import { Bomb } from './Bomb';
import { Explosion } from './Explosion';
import { spawnWreckage, WRECK_PALETTES } from './Wreckage';
import { Trail } from './Trail';
import { useStore } from '../../state/store';

// Per-type stats. Models live in makeEnemyMesh().
export type EnemyType = 'standard' | 'scout' | 'bomber' | 'ace';
interface EnemyStats {
  speed: number;
  turnRate: number;        // rad/s
  hp: number;
  fireInterval: number;    // sec
  shootCone: number;       // deg
  shootRange: number;
  radius: number;          // sphere fallback
  obb: { x: number; y: number; z: number; zFront?: number };
  scoreValue: number;
  /** Visual scale multiplier on the shared biplane mesh (only for type 'standard'). */
  meshScale: number;
}
const STATS: Record<EnemyType, EnemyStats> = {
  standard: { speed: 62, turnRate: 0.70, hp: 3, fireInterval: 1.0, shootCone: 24, shootRange: 700,
              radius: 6, obb: { x: 10.5, y: 2.2, z: 6.25, zFront: 3.0 }, scoreValue: 10, meshScale: 2.2 },
  scout:    { speed: 88, turnRate: 1.10, hp: 2, fireInterval: 1.5, shootCone: 20, shootRange: 600,
              radius: 5, obb: { x: 8.5,  y: 2.0, z: 5.5,  zFront: 2.6 }, scoreValue: 20, meshScale: 1.8 },
  bomber:   { speed: 42, turnRate: 0.32, hp: 8, fireInterval: 1.2, shootCone: 26, shootRange: 800,
              radius: 12, obb: { x: 16.5, y: 3.5, z: 9.0, zFront: 4.5 }, scoreValue: 50, meshScale: 3.2 },
  ace:      { speed: 76, turnRate: 1.30, hp: 25, fireInterval: 0.6, shootCone: 22, shootRange: 850,
              radius: 7, obb: { x: 9.0,  y: 3.0, z: 6.5,  zFront: 3.4 }, scoreValue: 1000, meshScale: 2.0 },
};

/** Helper: find the closest village that hasn't been bombed yet. */
function nearestLiveVillage(from: THREE.Vector3, ctx: EntityCtx) {
  let best = null;
  let bestD = Infinity;
  for (const v of ctx.game.gameScene.villages) {
    if (v.destroyed) continue;
    const dx = v.position.x - from.x;
    const dz = v.position.z - from.z;
    const d = Math.hypot(dx, dz);
    if (d < bestD) { bestD = d; best = v; }
  }
  return best;
}

/** Difficulty factor per wave — rookies on wave 1, veterans from wave 6, and pilots keep
 *  SLOWLY sharpening after that (cap 1.35 at wave ~18) so the back half of the campaign
 *  doesn't flatline the moment the early-wave training wheels come off. */
function difficulty(wave: number): number {
  if (wave <= 1) return 0.35;
  if (wave === 2) return 0.50;
  if (wave === 3) return 0.65;
  if (wave === 4) return 0.80;
  if (wave === 5) return 0.92;
  return Math.min(1.35, 1.0 + (wave - 6) * 0.03);
}
// When closer than MERGE_DIST, the enemy commits to a fly-through ("merge") instead of
// gluing itself to the player's six. It picks a pass-offset, holds straight for
// MERGE_DURATION, then resumes pursuit — giving the player a clean shot and a real
// chance to break contact.
const MERGE_DIST = 260;
const MERGE_DURATION = 2.0;
const REACQUIRE_DELAY = 1.6;
/** If the enemy has been circling (close and not gaining angle) for this long, it disengages. */
const CIRCLE_BREAK_SECS = 4.5;
const CIRCLE_BREAK_DIST = 500;
/** Ground-strafe targeting: fighters look this far for an allied ground unit to strafe, but only
 *  when no flying friendly is within STRAFE_AIR_PRIORITY (air threats take priority over ground). */
const STRAFE_SEEK_RANGE = 1400;
const STRAFE_AIR_PRIORITY = 700;

type AIState = 'pursue' | 'shoot' | 'evade' | 'merge' | 'reacquire';

const _epTrail = new THREE.Vector3();
// Per-frame steering scratch — with up to 16 enemies updating every frame, allocating these
// per update was the biggest GC churn in the entity loop.
const _toTarget = new THREE.Vector3();
const _cross = new THREE.Vector3();
const _q = new THREE.Quaternion();

export class EnemyPlane extends Entity {
  forward = new THREE.Vector3(0, 0, -1);
  state: AIState = 'pursue';
  evadeUntil = 0;
  mergeUntil = 0;
  reacquireUntil = 0;
  closeSince = -1; // timestamp when we first got inside CIRCLE_BREAK_DIST (reset if we leave)
  targetId = 0;    // friendly currently being dogfought (kept with hysteresis)
  fireCooldown = 0;
  passive = false; // Ghost Squadron apparitions: drift, never fire, still shootable
  private weaponsSafe = false; // opening-mission contacts maneuver but cannot shoot or bomb
  dying = false;
  dieTimer = 0;
  private trail = new Trail(40, 0xf0f4ff, 0.42);
  private _trailAdded = false;

  private prop: THREE.Object3D | null = null;
  private _smokeTimer = 0;
  private impacted = false;
  private impactTimer = 0;
  private bombTimer = 0;
  private strafingGround = false; // true while diving on a ground unit (allows a lower floor)
  readonly type: EnemyType;
  /** Cruise speed exposed for the audio system's Doppler calc. */
  readonly speed: number;
  private hpMax = 1;

  constructor(pos: THREE.Vector3, type: EnemyType = 'standard') {
    super('enemy');
    this.type = type;
    const cfg = STATS[type];
    // Aces are wave-3 bosses on first meeting but reappear as finale escorts — scale like a boss.
    this.hp = type === 'ace' ? Math.round(cfg.hp * bossHpScale()) : cfg.hp;
    this.hpMax = this.hp;
    this.radius = cfg.radius;
    this.speed = cfg.speed;
    // Multi-part collision: a narrow fuselage spine, the wide thin wing plane, and a
    // small tail. Bullets passing through the empty air between fuselage and wingtip
    // (or behind the wings out near the tail laterally) now miss instead of hitting a
    // single big box. Nose is along local -Z; tail is +Z. All parts route to damage().
    const ox = cfg.obb.x, oy = cfg.obb.y;
    const zFront = cfg.obb.zFront ?? cfg.obb.z * 0.5;
    const zBack = cfg.obb.z * 1.5;
    this.hitParts = [
      { id: 'fuselage',
        box: new THREE.Box3(new THREE.Vector3(-ox * 0.22, -oy, -zFront), new THREE.Vector3(ox * 0.22, oy * 0.75, zBack)),
        object: this.group },
      { id: 'wings',
        box: new THREE.Box3(new THREE.Vector3(-ox, -oy * 0.5, -zFront * 0.5), new THREE.Vector3(ox, oy * 0.6, zBack * 0.45)),
        object: this.group },
      { id: 'tail',
        box: new THREE.Box3(new THREE.Vector3(-ox * 0.42, -oy * 0.5, zBack * 0.55), new THREE.Vector3(ox * 0.42, oy * 0.95, zBack)),
        object: this.group },
    ];
    this.group.position.copy(pos);
    const mesh = makeEnemyMesh(type);
    mesh.scale.setScalar(cfg.meshScale);
    this.prop = (mesh.userData.prop as THREE.Object3D | undefined) ?? null;
    this.group.add(mesh);
    this.group.lookAt(0, pos.y, 0);
  }

  /** Turn an enemy into a no-fail training contact. Passive scouts hold a simple straight line;
   *  bombers still fly their normal approach so the interception reads as a real threat. */
  makeTrainingTarget(passive = false) {
    this.trainingTarget = true;
    this.weaponsSafe = true;
    this.passive = passive;
  }

  /** Ghost Squadron: fade to spectral translucency and go passive (drift, never fire). Materials
   *  are cloned first so the shared meshes' materials aren't affected. */
  makeGhostly() {
    this.passive = true;
    this.group.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.material) return;
      const fade = (mat: THREE.Material) => { const c = mat.clone(); c.transparent = true; c.opacity = 0.38; c.depthWrite = false; return c; };
      mesh.material = Array.isArray(mesh.material) ? mesh.material.map(fade) : fade(mesh.material);
    });
  }

  /** Flying Circus: repaint this plane in a garish livery (cloned material, so it's per-plane). */
  setLivery(color: number) {
    this.group.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.material) return;
      const tint = (mat: THREE.Material) => { const c = mat.clone(); const cm = c as THREE.MeshStandardMaterial; if (cm.color) cm.color.setHex(color); return c; };
      mesh.material = Array.isArray(mesh.material) ? mesh.material.map(tint) : tint(mesh.material);
    });
  }

  update(ctx: EntityCtx) {
    const { game, dt, now } = ctx;
    // Passive (ghost apparition): no AI, no fire — just drift forward eerily.
    if (this.passive) { this.group.translateZ(-this.speed * 0.35 * dt); return; }
    const wave = useStore.getState().wave;
    const diff = difficulty(wave);
    // Early-wave rookies turn slower, fire less often, shoot from a narrower cone
    // (less twitch-reactive), and engage from closer in so the player has reaction time.
    const cfg = STATS[this.type];
    // Aces are immune to early-wave dumbing — they're meant to be a wall. A WOUNDED ace
    // (under half HP) gets desperate: faster, tighter, meaner — the kill must be earned twice.
    const effDiff = this.type === 'ace' ? (this.hp <= this.hpMax / 2 ? 1.25 : 1.0) : diff;
    // Early-wave rookies are dumbed down, but NOT to the point of never shooting: keep a usable
    // turn rate and a wide-enough firing cone so they visibly open fire when roughly pointed at a
    // target (their rookie SPREAD below still makes them miss a lot — fire is seen, not lethal).
    const turnRate = cfg.turnRate * (0.7 + 0.3 * effDiff);
    const shootConeDeg = cfg.shootCone * (0.85 + 0.15 * effDiff);
    const shootRange = cfg.shootRange * (0.72 + 0.28 * effDiff);
    const fireInterval = cfg.fireInterval / Math.max(0.55, effDiff);

    if (this.prop) this.prop.rotation.z += 25 * dt;

    if (this.dying) {
      if (this.impacted) {
        // Ground wreck: no motion, just a lingering smoke column. Remove after a while so
        // wrecks don't pile up and eat draw calls.
        this.impactTimer += dt;
        this._smokeTimer += dt;
        if (this._smokeTimer >= 0.18) {
          this._smokeTimer = 0;
          new Explosion(
            this.group.position.clone().add(new THREE.Vector3((Math.random() - 0.5) * 4, 2, (Math.random() - 0.5) * 4)),
            18,
            3.0,
            'smoke',
          ).setDrift(new THREE.Vector3(0, 7, 0)).addTo(ctx);
        }
        if (this.impactTimer > 20) this.dead = true;
        return;
      }

      this.dieTimer += dt;
      // spiral down
      this.group.rotation.z += 3 * dt;
      this.group.position.y -= 30 * dt * (1 + this.dieTimer * 0.5);
      this.group.position.addScaledVector(this.forward, this.speed * 0.5 * dt);
      // Smoke trail
      this._smokeTimer += dt;
      if (this._smokeTimer >= 0.06) {
        this._smokeTimer = 0;
        new Explosion(this.group.position.clone(), 12, 2.2, 'smoke')
          .setDrift(new THREE.Vector3(0, 8, 0))
          .addTo(ctx);
        if (Math.random() < 0.35) {
          new Explosion(this.group.position.clone(), 6, 0.4, 'fire').addTo(ctx);
        }
      }
      // Ground impact — shatter into tumbling debris that cooks off across the field.
      const crashY = ctx.game.gameScene.heightAt(this.group.position.x, this.group.position.z);
      if (this.group.position.y < crashY) {
        const pos = this.group.position.clone(); pos.y = crashY;
        const baseVel = new THREE.Vector3(this.forward.x, -0.3, this.forward.z).multiplyScalar(26);
        spawnWreckage(ctx, pos, 'plane', WRECK_PALETTES.enemyPlane, { baseVel, count: 7, boom: 1.1, impact: { radius: 34, damage: 5 } });
        this.dead = true;
        return;
      } else if (this.dieTimer > 8) {
        // Fallback: if we've been falling a long time without hitting the ground plane,
        // just remove it to avoid a drifting ghost.
        this.dead = true;
      }
      return;
    }

    // Pick which friendly to engage. Keep the current target (hysteresis) unless it's
    // gone or another friendly is meaningfully closer — so the squadron actually draws
    // fire instead of every enemy piling onto whoever the player happens to be flying.
    let foe = game.friendlyById(this.targetId);
    if (foe && foe.kind !== 'player') foe = null; // only dogfight flying friendlies
    const nearest = game.nearestFriendlyPlane(this.group.position);
    if (!foe) {
      foe = nearest;
    } else if (nearest !== foe) {
      const dCur = foe.position.distanceToSquared(this.group.position);
      const dNew = nearest.position.distanceToSquared(this.group.position);
      if (dNew < dCur * 0.6) foe = nearest; // only switch if clearly closer
    }
    this.targetId = foe.id;

    // Bombers prefer live village targets; if no villages remain, they fall back to
    // attacking the chosen foe. Other types always go for the foe.
    let targetPos: THREE.Vector3 = foe.position;
    let bombingRun = false;
    this.strafingGround = false;
    if (this.type === 'bomber') {
      const village = nearestLiveVillage(this.group.position, ctx);
      if (village) {
        // Bombers loiter HIGH over the village — keep their altitude unchanged. Targeting
        // the village center at ground level made them pitch down through the terrain.
        targetPos = new THREE.Vector3(village.position.x, this.group.position.y, village.position.z);
        bombingRun = true;
      }
    } else if (this.type === 'standard' || this.type === 'scout') {
      // Fighter biplanes strafe the player's GROUND defences (allied tanks + AA/trench guns)
      // when there's no flying friendly nearby to dogfight — dive on the gun line and rake it.
      const airThreatNear = foe.kind === 'player'
        && foe.position.distanceToSquared(this.group.position) < STRAFE_AIR_PRIORITY * STRAFE_AIR_PRIORITY;
      if (!airThreatNear) {
        const ground = game.nearestAllyGround(this.group.position, STRAFE_SEEK_RANGE);
        if (ground) { targetPos = ground.position; this.strafingGround = true; }
      }
    }
    const toPlayer = _toTarget.copy(targetPos).sub(this.group.position);
    const dist = toPlayer.length();
    toPlayer.normalize();

    // Current forward
    this.forward.set(0, 0, -1).applyQuaternion(this.group.quaternion);

    // Track how long we've been in the close-combat bubble. If the player has managed to
    // hold us in there without being killed, we've lost the initiative — disengage.
    if (dist < CIRCLE_BREAK_DIST) {
      if (this.closeSince < 0) this.closeSince = now;
    } else {
      this.closeSince = -1;
    }
    const beenCircling = this.closeSince >= 0 && (now - this.closeSince) > CIRCLE_BREAK_SECS;

    // Decide state
    if (now < this.evadeUntil) {
      this.state = 'evade';
    } else if (now < this.mergeUntil) {
      this.state = 'merge';
    } else if (now < this.reacquireUntil) {
      this.state = 'reacquire';
    } else if (beenCircling) {
      // Disengage hard — fly away and reset the close-timer. Prevents the dreaded
      // "both planes circle forever" loop.
      this.mergeUntil = now + 2.5;
      this.reacquireUntil = this.mergeUntil + 2.5;
      this.closeSince = -1;
      this.state = 'merge';
    } else if (dist < MERGE_DIST) {
      // Commit to a merge: fly straight through for a moment, don't try to match the
      // player's turn at point-blank (that's where the AI felt impossible).
      this.mergeUntil = now + MERGE_DURATION;
      this.reacquireUntil = this.mergeUntil + REACQUIRE_DELAY;
      this.state = 'merge';
    } else {
      const coneCos = Math.cos(THREE.MathUtils.degToRad(shootConeDeg));
      const inCone = this.forward.dot(toPlayer) > coneCos;
      this.state = (inCone && dist < shootRange) ? 'shoot' : 'pursue';
    }

    // Choose steering target by state (evade flips toPlayer in place — it's scratch).
    let target: THREE.Vector3 | null;
    if (this.state === 'evade') {
      target = toPlayer.multiplyScalar(-1);
    } else if (this.state === 'merge') {
      // Hold heading — no steering input. Momentum carries through.
      target = null;
    } else if (this.state === 'reacquire') {
      // Slow, lazy turn back toward the player; lets the player actually break away.
      target = toPlayer;
    } else {
      target = toPlayer;
    }

    if (target) {
      const rate = this.state === 'reacquire' ? turnRate * 0.4 : turnRate;
      const cross = _cross.crossVectors(this.forward, target);
      const dot = THREE.MathUtils.clamp(this.forward.dot(target), -1, 1);
      const angleBetween = Math.acos(dot);
      if (angleBetween > 1e-3) {
        const axis = cross.normalize();
        const maxStep = rate * dt;
        const step = Math.min(angleBetween, maxStep);
        this.group.quaternion.premultiply(_q.setFromAxisAngle(axis, step));
        this.forward.set(0, 0, -1).applyQuaternion(this.group.quaternion);
      }
    }

    // Advance
    this.group.position.addScaledVector(this.forward, this.speed * dt);
    // Hard altitude floor — never let an enemy plane sink through the terrain. Bombers
    // get a higher floor so they stay in cruise altitude.
    // Floor: bombers stay high; fighters cruise at 60 but drop to ~12 above the deck while
    // strafing a ground unit so their dive can actually reach it. (Terrain top ≈ -468; units
    // sit on it, so a low floor relative to the unit lets the run rake across the gun line.)
    const minY = this.type === 'bomber' ? 200 : (this.strafingGround ? targetPos.y + 12 : 60);
    if (this.group.position.y < minY) this.group.position.y = minY;

    // Contrail off the tail.
    if (!this._trailAdded) { ctx.scene.add(this.trail.object); this._trailAdded = true; }
    _epTrail.copy(this.group.position).addScaledVector(this.forward, -3.0);
    this.trail.update(_epTrail);

    // Fire only in 'shoot' state
    this.fireCooldown -= dt;
    // Bomb-drop logic: only bombers, only when actively bombing a village. We project
    // ballistic free-fall from current position with current horizontal velocity; if the
    // landing point lies within the village radius, release one bomb. Cooldown prevents
    // dumping a whole stick at once.
    this.bombTimer -= dt;
    if (!this.weaponsSafe && this.type === 'bomber' && bombingRun && this.bombTimer <= 0) {
      const village = nearestLiveVillage(this.group.position, ctx);
      if (village) {
        const G = 26;
        const altitude = this.group.position.y - village.position.y;
        if (altitude > 30) {
          const tFall = Math.sqrt((2 * altitude) / G);
          // horizontal velocity (xz only)
          const vx = this.forward.x * this.speed;
          const vz = this.forward.z * this.speed;
          const landX = this.group.position.x + vx * tFall;
          const landZ = this.group.position.z + vz * tFall;
          const horizDist = Math.hypot(landX - village.position.x, landZ - village.position.z);
          if (horizDist <= village.radius * 1.1) {
            const initial = new THREE.Vector3(vx, 0, vz);
            ctx.game.add(new Bomb(this.group.position.clone(), initial, this.id));
            this.bombTimer = 4 + Math.random() * 2;
          }
        }
      }
    }

    if (!this.weaponsSafe && this.state === 'shoot' && this.fireCooldown <= 0) {
      const pos = this.group.position.clone().add(this.forward.clone().multiplyScalar(3));
      // Rookie pilots can't shoot straight — add a random spread (larger at low difficulty).
      const spread = (1 - diff) * 0.09; // rad; full rookies scatter ~5° off-axis
      const vel = this.forward.clone().multiplyScalar(240).addScaledVector(this.forward, this.speed);
      if (spread > 0) {
        vel.x += (Math.random() - 0.5) * spread * vel.length();
        vel.y += (Math.random() - 0.5) * spread * vel.length();
      }
      ctx.game.add(new Bullet(pos, vel, this.id, 'enemy'));
      this.fireCooldown = fireInterval * ctx.game.enemyFireScale; // <1 during Blood-Red Dawn (sun advantage)
    }
  }

  alive(): boolean { return !this.dying; }

  dispose(): void {
    this.trail.dispose();
    super.dispose();
  }

  damage(amount: number, ctx: EntityCtx): void {
    this.hp -= amount;
    if (this.hp <= 0) {
      this.startDying(ctx);
    } else {
      // evade briefly on hit
      this.evadeUntil = ctx.now + 1.2;
      this.state = 'evade';
    }
  }

  private startDying(ctx: EntityCtx) {
    if (this.dying) return;
    this.dying = true;
    useStore.getState().registerKill(STATS[this.type].scoreValue);
    // Shatter on the spot — the kill bursts into tumbling, burning debris that rains down
    // and cooks off, right where the player is already looking. (Much more visible than the
    // old long spiral-to-ground, where the wreck landed off-screen seconds later.)
    const baseVel = this.forward.clone().multiplyScalar(this.speed).add(new THREE.Vector3(0, -10, 0));
    // The wreckage's first chunk to land craters the crash site (trees/buildings/units).
    spawnWreckage(ctx, this.group.position.clone(), 'plane', WRECK_PALETTES.enemyPlane,
      { baseVel, count: 8, boom: 1.2, impact: { radius: 34, damage: 5 } });
    this.dead = true;
  }
}

function darken(hex: number, factor: number): number {
  const r = (hex >> 16) & 0xff;
  const g = (hex >> 8) & 0xff;
  const b = hex & 0xff;
  return ((r * factor) & 0xff) << 16 | ((g * factor) & 0xff) << 8 | (b * factor) & 0xff;
}

/** Airfoil cross-section shape (approximate NACA 4412-ish), centered around Z=0, chord 1. */
function airfoilShape(thickness: number): THREE.Shape {
  const s = new THREE.Shape();
  const N = 24;
  // Top surface: leading edge → trailing edge
  s.moveTo(-0.5, 0);
  for (let i = 1; i <= N; i++) {
    const t = i / N; // 0..1 along chord
    // NACA-ish thickness distribution (simplified)
    const y = thickness * (0.2969 * Math.sqrt(t) - 0.1260 * t - 0.3516 * t * t + 0.2843 * t * t * t - 0.1015 * t * t * t * t);
    const x = -0.5 + t;
    s.lineTo(x, y);
  }
  // Bottom surface: trailing edge → leading edge
  for (let i = N - 1; i >= 0; i--) {
    const t = i / N;
    const y = -thickness * 0.6 * (0.2969 * Math.sqrt(t) - 0.1260 * t - 0.3516 * t * t + 0.2843 * t * t * t - 0.1015 * t * t * t * t);
    const x = -0.5 + t;
    s.lineTo(x, y);
  }
  s.closePath();
  return s;
}

/** Build an airfoil-shaped wing: airfoil cross-section extruded along X (span), tapered tips. */
function makeAirfoilWing(span: number, chord: number, thickness: number, topMat: THREE.Material, underMat: THREE.Material, trimMat: THREE.Material): THREE.Group {
  const grp = new THREE.Group();
  const shape = airfoilShape(thickness / chord); // normalized thickness ratio

  // Extrude along X by using LatheGeometry? No — we need variable width for taper.
  // Simpler: build a custom BufferGeometry with stations along the span that pinch toward tips.
  const stations = 22;
  const profPts = shape.getPoints(28);
  const vertsPerStation = profPts.length;

  const positions: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];

  const halfSpan = span / 2;
  for (let s = 0; s <= stations; s++) {
    const t = s / stations;
    // Taper toward the tips: rounded elliptical planform
    const x = -halfSpan + t * span;
    const taper = Math.sqrt(1 - Math.pow((x) / halfSpan, 2)); // ellipse shape: full chord near root, tapering smoothly to ~0 at tip
    const localChord = chord * (0.55 + 0.45 * taper);
    for (const p of profPts) {
      positions.push(x, p.y * chord * (0.55 + 0.45 * taper) / thickness * thickness, p.x * localChord);
      normals.push(0, 1, 0); // simplified
    }
  }
  for (let s = 0; s < stations; s++) {
    for (let i = 0; i < vertsPerStation - 1; i++) {
      const a = s * vertsPerStation + i;
      const b = s * vertsPerStation + i + 1;
      const c = (s + 1) * vertsPerStation + i + 1;
      const d = (s + 1) * vertsPerStation + i;
      indices.push(a, b, c);
      indices.push(a, c, d);
    }
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geom.setIndex(indices);
  geom.computeVertexNormals();

  const topMesh = new THREE.Mesh(geom, topMat);
  grp.add(topMesh);

  // Underside darker shade: duplicate geometry flipped (simpler: second mesh with back-facing)
  // For simplicity we just use the top material; a proper two-material split would require
  // groups — we keep it single-material for perf. underMat/trimMat are retained for future use.
  void underMat;

  // Trailing-edge trim strip
  const edge = new THREE.Mesh(new THREE.BoxGeometry(span, thickness * 1.05, chord * 0.14, 40, 2, 2), trimMat);
  edge.position.z = chord * 0.43;
  grp.add(edge);

  return grp;
}

/** Wood propeller blade with a proper airfoil-ish twist. */
function makePropBladeGeom(length: number, maxW: number): THREE.BufferGeometry {
  const stations = 10;
  const positions: number[] = [];
  const indices: number[] = [];
  // Each station has 4 vertices forming an elongated diamond-ish cross-section
  for (let i = 0; i <= stations; i++) {
    const t = i / stations;
    const x = (t - 0.5) * length;
    // Blade chord widens mid-span and narrows at root & tip
    const w = maxW * Math.sin(Math.PI * t) * 0.95 + maxW * 0.1;
    // Twist: leading edge pitched forward near hub, flat near tip
    const twist = (1 - t) * 0.45;
    const th = 0.05;
    // 4 vertices: leading-top, trailing-top, trailing-bottom, leading-bottom
    positions.push(x, th, -w / 2 + Math.sin(twist) * th);
    positions.push(x, th, w / 2 + Math.sin(twist) * th);
    positions.push(x, -th, w / 2 - Math.sin(twist) * th);
    positions.push(x, -th, -w / 2 - Math.sin(twist) * th);
  }
  for (let i = 0; i < stations; i++) {
    const b = i * 4;
    for (let k = 0; k < 4; k++) {
      const a = b + k;
      const bb = b + (k + 1) % 4;
      const c = b + 4 + (k + 1) % 4;
      const d = b + 4 + k;
      indices.push(a, bb, c);
      indices.push(a, c, d);
    }
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geom.setIndex(indices);
  geom.computeVertexNormals();
  return geom;
}

type Livery = { fuse: number; wing: number; trim: number; rudderA: number; rudderB: number };

const LIVERIES: Livery[] = [
  // British khaki with RAF roundel colors for rudder stripes
  { fuse: 0xa38c58, wing: 0x8f7b48, trim: 0x3a2e1c, rudderA: 0x1e3a7a, rudderB: 0xc02c2c },
  // French olive with blue/white/red (reversed on model)
  { fuse: 0x6e7a3c, wing: 0x5f6a33, trim: 0x2e2a18, rudderA: 0x1e3a7a, rudderB: 0xefefef },
  // Silver-doped Sopwith-ish
  { fuse: 0xcfc9b8, wing: 0xbbb6a5, trim: 0x2a2620, rudderA: 0x1e3a7a, rudderB: 0xc02c2c },
];

/** Dispatch to the correct mesh factory by enemy type. */
function makeEnemyMesh(type: EnemyType): THREE.Group {
  switch (type) {
    case 'scout':  return makeScoutMesh();
    case 'bomber': return makeBomberMesh();
    case 'ace':    return makeAceMesh();
    default:       return makeBiplane();
  }
}

function makeBiplane(): THREE.Group {
  const g = new THREE.Group();
  // Plane is modeled facing -Z (forward), with +Y up, +X out the right wing.

  const livery = LIVERIES[Math.floor(Math.random() * LIVERIES.length)];
  const fuseMat = new THREE.MeshStandardMaterial({ color: livery.fuse, roughness: 0.75 });
  const fuseBellyMat = new THREE.MeshStandardMaterial({ color: darken(livery.fuse, 0.7), roughness: 0.85 });
  const wingMat = new THREE.MeshStandardMaterial({ color: livery.wing, roughness: 0.8 });
  const wingUnderMat = new THREE.MeshStandardMaterial({ color: darken(livery.wing, 0.78), roughness: 0.85 });
  const trimMat = new THREE.MeshStandardMaterial({ color: livery.trim, roughness: 0.85 });
  const metalMat = new THREE.MeshStandardMaterial({ color: 0x4a4944, roughness: 0.38, metalness: 0.7 });
  const exhaustMat = new THREE.MeshStandardMaterial({ color: 0x2a201a, roughness: 0.6, metalness: 0.4 });
  const rubberMat = new THREE.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.95 });
  const windscreenMat = new THREE.MeshStandardMaterial({ color: 0x202830, roughness: 0.25, metalness: 0.3, transparent: true, opacity: 0.55 });

  // --- Fuselage (upper shell + lighter belly) ---
  // Tapered top using LatheGeometry for a clean airfoil-like silhouette.
  const fuseTopPts: THREE.Vector2[] = [];
  for (let i = 0; i <= 32; i++) {
    const t = i / 32;
    const z = -2.3 + t * 5.7; // z range along the fuselage
    // Radius tapers larger in the middle and smaller at the nose/tail.
    const r = 0.55 * Math.sin(Math.PI * t) + 0.12;
    fuseTopPts.push(new THREE.Vector2(r, z));
  }
  // Use CylinderGeometry via a parametric function instead — simpler.
  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.35, 4.6, 80, 12), fuseMat);
  body.rotation.x = Math.PI / 2;
  body.position.set(0, 0, 0.4);
  g.add(body);
  // Lighter belly half (a half-cylinder of slightly smaller radius, offset down)
  const belly = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.32, 4.4, 80, 8, false, 0, Math.PI), fuseBellyMat);
  belly.rotation.z = Math.PI / 2;
  belly.rotation.y = 0;
  belly.position.set(0, -0.03, 0.4);
  g.add(belly);

  // Nose cone
  const nose = new THREE.Mesh(new THREE.ConeGeometry(0.6, 1.2, 80, 6), metalMat);
  nose.rotation.x = -Math.PI / 2;
  nose.position.set(0, 0, -2.4);
  g.add(nose);

  // Tail cone
  const tailCone = new THREE.Mesh(new THREE.ConeGeometry(0.35, 1.0, 64, 6), fuseMat);
  tailCone.rotation.x = Math.PI / 2;
  tailCone.position.set(0, 0, 3.2);
  g.add(tailCone);

  // Open cockpit
  const pit = new THREE.Mesh(
    new THREE.CylinderGeometry(0.35, 0.35, 0.4, 80, 4),
    new THREE.MeshStandardMaterial({ color: 0x120c08, roughness: 1 }),
  );
  pit.position.set(0, 0.4, 0.3);
  pit.rotation.x = Math.PI / 2;
  pit.scale.set(1, 1, 1.8);
  g.add(pit);

  // Windscreen — a small curved pane in front of the pilot.
  const screen = new THREE.Mesh(new THREE.CylinderGeometry(0.36, 0.36, 0.25, 40, 2, false, -Math.PI / 2 - 0.6, 1.2), windscreenMat);
  screen.rotation.x = Math.PI / 2;
  screen.position.set(0, 0.5, -0.2);
  g.add(screen);

  // Pilot silhouette (leather helmet + shoulders)
  const leatherMat = new THREE.MeshStandardMaterial({ color: 0x3a2a1a, roughness: 0.9 });
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.22, 40, 28), leatherMat);
  head.position.set(0, 0.76, 0.35);
  g.add(head);
  const shoulders = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.24, 0.4, 6, 4, 6), leatherMat);
  shoulders.position.set(0, 0.55, 0.35);
  g.add(shoulders);
  // Goggles hint — a thin dark band on the helmet
  const gog = new THREE.Mesh(new THREE.CylinderGeometry(0.23, 0.23, 0.06, 40, 2), new THREE.MeshStandardMaterial({ color: 0x101010, roughness: 0.4 }));
  gog.rotation.z = Math.PI / 2;
  gog.position.set(0, 0.8, 0.35);
  g.add(gog);

  // --- Propeller ---
  const prop = new THREE.Group();
  const bladeGeom = makePropBladeGeom(2.0, 0.18);
  for (let i = 0; i < 2; i++) {
    const blade = new THREE.Mesh(bladeGeom, trimMat);
    blade.rotation.z = i === 0 ? 0 : Math.PI / 2;
    prop.add(blade);
  }
  const hub = new THREE.Mesh(new THREE.SphereGeometry(0.18, 64, 40), metalMat);
  prop.add(hub);
  prop.position.set(0, 0, -3.05);
  g.add(prop);
  g.userData.prop = prop;

  // Prop disc
  const propDisc = new THREE.Mesh(
    new THREE.CircleGeometry(1.05, 192),
    new THREE.MeshBasicMaterial({ color: 0x888888, transparent: true, opacity: 0.12, side: THREE.DoubleSide, depthWrite: false }),
  );
  propDisc.position.set(0, 0, -3.15);
  g.add(propDisc);

  // Exhaust stacks along the nose sides (two short pipes)
  for (const side of [-1, 1]) {
    const exh = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.08, 0.55, 24, 2), exhaustMat);
    exh.rotation.x = Math.PI / 2;
    exh.position.set(side * 0.35, 0.12, -1.95);
    g.add(exh);
  }

  // --- Wings (airfoil cross-section, tapered tips) ---
  const upper = makeAirfoilWing(9.2, 1.8, 0.22, wingMat, wingUnderMat, trimMat);
  upper.position.set(0, 1.35, -0.2);
  g.add(upper);
  const lower = makeAirfoilWing(8.4, 1.7, 0.2, wingMat, wingUnderMat, trimMat);
  lower.position.set(0, -0.55, 0.45);
  g.add(lower);

  // --- Roundels on wings (top of upper, bottom of lower) ---
  // polygonOffset pushes the decals toward the camera in the depth buffer, eliminating
  // the z-fighting shimmer where they sit against the wing fabric.
  const roundelOuter = new THREE.MeshBasicMaterial({ color: livery.rudderA, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
  const roundelMid = new THREE.MeshBasicMaterial({ color: 0xf2f2f2, polygonOffset: true, polygonOffsetFactor: -3, polygonOffsetUnits: -3 });
  const roundelInner = new THREE.MeshBasicMaterial({ color: livery.rudderB, polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4 });
  const drawRoundel = (parent: THREE.Object3D, x: number, y: number, z: number, flip: boolean) => {
    const r = new THREE.Group();
    const d = 0.65;
    const a = new THREE.Mesh(new THREE.CircleGeometry(d, 80), roundelOuter);
    const b = new THREE.Mesh(new THREE.CircleGeometry(d * 0.66, 80), roundelMid);
    const c = new THREE.Mesh(new THREE.CircleGeometry(d * 0.33, 64), roundelInner);
    a.rotation.x = -Math.PI / 2;
    b.rotation.x = -Math.PI / 2;
    c.rotation.x = -Math.PI / 2;
    b.position.y = 0.001;
    c.position.y = 0.002;
    if (flip) { a.rotation.x = Math.PI / 2; b.rotation.x = Math.PI / 2; c.rotation.x = Math.PI / 2; b.position.y = -0.001; c.position.y = -0.002; }
    r.add(a); r.add(b); r.add(c);
    r.position.set(x, y, z);
    parent.add(r);
  };
  drawRoundel(upper, -2.9, 0.12, 0, false);
  drawRoundel(upper, 2.9, 0.12, 0, false);
  drawRoundel(lower, -2.6, -0.12, 0, true);
  drawRoundel(lower, 2.6, -0.12, 0, true);

  // --- Interplane struts ---
  const strut = (x: number, zOffset = 0) => {
    const s = new THREE.Mesh(new THREE.BoxGeometry(0.08, 1.9, 0.14, 2, 8, 2), trimMat);
    s.position.set(x, 0.4, zOffset);
    return s;
  };
  for (const dx of [-2.7, -1.1, 1.1, 2.7]) {
    g.add(strut(dx, -0.4));
    g.add(strut(dx, 0.45));
  }
  // Cross-bracing wire hint (thin diagonals)
  for (const dx of [-2.1, 2.1]) {
    const wire = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.015, 2.1, 16), trimMat);
    wire.position.set(dx, 0.4, 0);
    wire.rotation.z = 0.15;
    g.add(wire);
  }

  // --- Tail surfaces ---
  const hstab = new THREE.Mesh(new THREE.BoxGeometry(3.4, 0.14, 1.2, 12, 2, 4), wingMat);
  hstab.position.set(0, 0.15, 3.0);
  g.add(hstab);
  const elevatorTrim = new THREE.Mesh(new THREE.BoxGeometry(3.4, 0.16, 0.35, 12, 2, 2), trimMat);
  elevatorTrim.position.set(0, 0.15, 3.5);
  g.add(elevatorTrim);

  const fin = new THREE.Mesh(new THREE.BoxGeometry(0.15, 1.2, 1.1, 2, 6, 4), wingMat);
  fin.position.set(0, 0.75, 3.1);
  g.add(fin);

  // Rudder stripes (three-color)
  const stripeMatA = new THREE.MeshStandardMaterial({ color: livery.rudderA, roughness: 0.9 });
  const stripeMatB = new THREE.MeshStandardMaterial({ color: 0xf2f2f2, roughness: 0.9 });
  const stripeMatC = new THREE.MeshStandardMaterial({ color: livery.rudderB, roughness: 0.9 });
  const stripeStack: [THREE.Material, number][] = [[stripeMatA, 0.3], [stripeMatB, 0.3], [stripeMatC, 0.3]];
  let rz = 3.65;
  for (const [m, w] of stripeStack) {
    const s = new THREE.Mesh(new THREE.BoxGeometry(0.17, 1.1, w, 2, 6, 2), m);
    s.position.set(0, 0.75, rz);
    g.add(s);
    rz += w;
  }

  // --- Landing gear ---
  const gearMat = new THREE.MeshStandardMaterial({ color: 0x2a2018, roughness: 0.95 });
  const axle = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 2.4, 32), gearMat);
  axle.rotation.z = Math.PI / 2;
  axle.position.set(0, -1.25, -0.6);
  g.add(axle);
  for (const dx of [-1.05, 1.05]) {
    const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.38, 0.38, 0.2, 56), rubberMat);
    wheel.rotation.z = Math.PI / 2;
    wheel.position.set(dx, -1.25, -0.6);
    g.add(wheel);
    // V-strut
    for (const dz of [-0.4, 0.4]) {
      const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 1.0, 24), gearMat);
      leg.position.set(dx * 0.6, -0.85, -0.6 + dz * 0.2);
      leg.rotation.z = dx > 0 ? 0.35 : -0.35;
      leg.rotation.x = dz > 0 ? 0.2 : -0.2;
      g.add(leg);
    }
  }

  // --- Forward-firing gun (Vickers over the cowling) ---
  const gunBarrel = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 1.1, 32), metalMat);
  gunBarrel.rotation.x = Math.PI / 2;
  gunBarrel.position.set(0.15, 0.55, -1.6);
  g.add(gunBarrel);

  g.traverse((o) => { (o as THREE.Mesh).castShadow = true; });
  return g;
}

/* ---------------------------------------------------------------------- *
 *  Scout — Sopwith Camel-like single-seat fighter. Smaller, faster,
 *  rotary engine cowl, single-bay biplane (one wing pair, not three).
 * ---------------------------------------------------------------------- */
function makeScoutMesh(): THREE.Group {
  const g = new THREE.Group();
  const fuse = new THREE.MeshStandardMaterial({ color: 0x9c8e60, roughness: 0.78 });
  const cowl = new THREE.MeshStandardMaterial({ color: 0x3a3a36, roughness: 0.4, metalness: 0.7 });
  const wing = new THREE.MeshStandardMaterial({ color: 0x807149, roughness: 0.8 });
  const trim = new THREE.MeshStandardMaterial({ color: 0x4d3b1f, roughness: 0.85 });
  const rubber = new THREE.MeshStandardMaterial({ color: 0x141414, roughness: 0.95 });
  const leather = new THREE.MeshStandardMaterial({ color: 0x3a2616, roughness: 0.9 });

  // Fuselage — short barrel up front, tapering to the tail.
  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.3, 4.2, 48, 6), fuse);
  body.rotation.x = Math.PI / 2;
  body.position.set(0, 0, 0.3);
  g.add(body);
  // Rotary engine cowl (drum at the nose)
  const cowlMesh = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.62, 0.55, 56, 2), cowl);
  cowlMesh.rotation.x = Math.PI / 2;
  cowlMesh.position.set(0, 0, -2.0);
  g.add(cowlMesh);
  // Tail
  const tail = new THREE.Mesh(new THREE.ConeGeometry(0.3, 0.9, 32), fuse);
  tail.rotation.x = Math.PI / 2;
  tail.position.set(0, 0, 2.85);
  g.add(tail);

  // Single-bay biplane wings (smaller wingspan than the standard)
  const upper = makeAirfoilWing(7.4, 1.4, 0.18, wing, wing, trim);
  upper.position.set(0, 1.1, -0.3);
  g.add(upper);
  const lower = makeAirfoilWing(6.8, 1.4, 0.18, wing, wing, trim);
  lower.position.set(0, -0.5, 0.25);
  g.add(lower);
  // Two outboard interplane struts only (single-bay)
  for (const dx of [-2.4, 2.4]) {
    const s = new THREE.Mesh(new THREE.BoxGeometry(0.07, 1.6, 0.12, 2, 6, 2), trim);
    s.position.set(dx, 0.3, 0);
    g.add(s);
  }

  // Cockpit & pilot
  const pit = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.32, 0.4, 48, 2), new THREE.MeshStandardMaterial({ color: 0x110b06, roughness: 1 }));
  pit.position.set(0, 0.45, 0.2);
  pit.rotation.x = Math.PI / 2;
  pit.scale.set(1, 1, 1.7);
  g.add(pit);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.2, 32, 20), leather);
  head.position.set(0, 0.7, 0.25);
  g.add(head);

  // Prop with twisted blades
  const prop = new THREE.Group();
  const bladeG = makePropBladeGeom(2.0, 0.16);
  for (let i = 0; i < 2; i++) {
    const b = new THREE.Mesh(bladeG, trim);
    b.rotation.z = i === 0 ? 0 : Math.PI / 2;
    prop.add(b);
  }
  const hub = new THREE.Mesh(new THREE.SphereGeometry(0.16, 32, 24), cowl);
  prop.add(hub);
  prop.position.set(0, 0, -2.32);
  g.add(prop);
  g.userData.prop = prop;

  // Round rondelle on each upper wing top (Allied roundel hint)
  const rondMat = new THREE.MeshBasicMaterial({ color: 0xc02c2c, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
  for (const dx of [-2.4, 2.4]) {
    const r = new THREE.Mesh(new THREE.CircleGeometry(0.55, 60), rondMat);
    r.rotation.x = -Math.PI / 2;
    r.position.set(dx, 1.21, -0.3);
    upper.add(r);
  }

  // Tail surfaces
  const hstab = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.1, 0.9, 24, 2, 6), wing);
  hstab.position.set(0, 0.05, 2.7);
  g.add(hstab);
  const fin = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.85, 0.9, 4, 8, 6), wing);
  fin.position.set(0, 0.5, 2.85);
  g.add(fin);

  // Landing gear — minimal
  for (const dx of [-0.85, 0.85]) {
    const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.3, 0.16, 36, 2), rubber);
    wheel.rotation.z = Math.PI / 2;
    wheel.position.set(dx, -1.05, -0.3);
    g.add(wheel);
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.85, 16, 2), trim);
    leg.position.set(dx * 0.5, -0.7, -0.3);
    leg.rotation.z = dx > 0 ? 0.4 : -0.4;
    g.add(leg);
  }
  // Single Vickers
  const gun = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.9, 24, 2), cowl);
  gun.rotation.x = Math.PI / 2;
  gun.position.set(0, 0.55, -1.5);
  g.add(gun);

  return g;
}

/* ---------------------------------------------------------------------- *
 *  Bomber — Gotha-like twin-engine heavy. Long fuselage, two engines on
 *  the lower wing, double biplane with bigger struts, gunner positions.
 * ---------------------------------------------------------------------- */
function makeBomberMesh(): THREE.Group {
  const g = new THREE.Group();
  const dark = new THREE.MeshStandardMaterial({ color: 0x4a4a44, roughness: 0.85 });
  const camo = new THREE.MeshStandardMaterial({ color: 0x5e6149, roughness: 0.85 });
  const engine = new THREE.MeshStandardMaterial({ color: 0x2a2a26, roughness: 0.4, metalness: 0.7 });
  const trim = new THREE.MeshStandardMaterial({ color: 0x2a2218, roughness: 0.9 });
  const rubber = new THREE.MeshStandardMaterial({ color: 0x121212, roughness: 0.95 });

  // Long, slab-sided fuselage
  const body = new THREE.Mesh(new THREE.BoxGeometry(1.4, 1.5, 7.5, 8, 8, 24), dark);
  body.position.set(0, 0, 0.5);
  g.add(body);
  // Nose taper (cone)
  const nose = new THREE.Mesh(new THREE.ConeGeometry(0.7, 1.4, 32, 4), dark);
  nose.rotation.x = -Math.PI / 2;
  nose.position.set(0, 0, -3.5);
  g.add(nose);
  // Tail
  const tailCone = new THREE.Mesh(new THREE.ConeGeometry(0.5, 1.3, 32, 4), dark);
  tailCone.rotation.x = Math.PI / 2;
  tailCone.position.set(0, 0, 4.7);
  g.add(tailCone);

  // Big upper and lower wings (long)
  const upper = makeAirfoilWing(13.5, 2.2, 0.28, camo, camo, trim);
  upper.position.set(0, 1.7, -0.4);
  g.add(upper);
  const lower = makeAirfoilWing(13.5, 2.2, 0.28, camo, camo, trim);
  lower.position.set(0, -0.4, 0.2);
  g.add(lower);

  // Twin engines on the lower wing
  const propGroup = new THREE.Group();
  for (const dx of [-3.5, 3.5]) {
    const nacelle = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.5, 1.8, 40, 4), engine);
    nacelle.rotation.x = Math.PI / 2;
    nacelle.position.set(dx, -0.15, -0.6);
    g.add(nacelle);
    const cowl = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.55, 0.4, 40, 2), engine);
    cowl.rotation.x = Math.PI / 2;
    cowl.position.set(dx, -0.15, -1.5);
    g.add(cowl);
    // Propeller (real twisted blades, individually animatable per engine)
    const prop = new THREE.Group();
    const bladeG = makePropBladeGeom(2.5, 0.2);
    for (let i = 0; i < 2; i++) {
      const blade = new THREE.Mesh(bladeG, trim);
      blade.rotation.z = i === 0 ? 0 : Math.PI / 2;
      prop.add(blade);
    }
    const hub = new THREE.Mesh(new THREE.SphereGeometry(0.18, 32, 24), engine);
    prop.add(hub);
    prop.position.set(dx, -0.15, -1.75);
    propGroup.add(prop);
  }
  g.add(propGroup);
  g.userData.prop = propGroup; // animated together

  // Multiple vertical struts between wings
  for (const dx of [-3.5, -1.5, 1.5, 3.5]) {
    const s1 = new THREE.Mesh(new THREE.BoxGeometry(0.1, 2.1, 0.18, 2, 8, 2), trim);
    s1.position.set(dx, 0.65, -0.1);
    g.add(s1);
  }

  // Open cockpit + dorsal gunner position
  const pit = new THREE.Mesh(new THREE.BoxGeometry(0.95, 0.5, 1.4, 6, 4, 8), new THREE.MeshStandardMaterial({ color: 0x100a06, roughness: 1 }));
  pit.position.set(0, 0.85, -0.6);
  g.add(pit);
  const gunner = new THREE.Mesh(new THREE.BoxGeometry(0.95, 0.5, 1.0, 6, 4, 6), new THREE.MeshStandardMaterial({ color: 0x100a06, roughness: 1 }));
  gunner.position.set(0, 0.85, 1.8);
  g.add(gunner);
  // Rear gun on a ring
  const rearGun = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 1.1, 24, 2), engine);
  rearGun.rotation.x = -0.4;
  rearGun.position.set(0, 1.3, 2.4);
  g.add(rearGun);

  // Tail surfaces — twin fins (Gotha had a single but visual emphasis)
  const hstab = new THREE.Mesh(new THREE.BoxGeometry(4.2, 0.15, 1.4, 28, 2, 8), camo);
  hstab.position.set(0, 0.1, 4.5);
  g.add(hstab);
  for (const dx of [-1.6, 1.6]) {
    const fin = new THREE.Mesh(new THREE.BoxGeometry(0.12, 1.1, 1.0, 4, 10, 6), camo);
    fin.position.set(dx, 0.65, 4.6);
    g.add(fin);
  }

  // Landing gear — twin pair
  for (const dx of [-3.5, 3.5]) {
    const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.42, 0.2, 56, 2), rubber);
    wheel.rotation.z = Math.PI / 2;
    wheel.position.set(dx, -1.3, -0.3);
    g.add(wheel);
    for (const dz of [-0.4, 0.4]) {
      const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 1.0, 20, 2), trim);
      leg.position.set(dx * 0.85, -0.85, -0.3 + dz * 0.2);
      leg.rotation.z = dx > 0 ? 0.3 : -0.3;
      leg.rotation.x = dz > 0 ? 0.2 : -0.2;
      g.add(leg);
    }
  }

  // Big crosses on the wings (this thing is German-only camo conceit; for Allied skins
  // we'd swap colors, but the bomber feels appropriate as a Gotha-style threat)
  const black = new THREE.MeshBasicMaterial({ color: 0x080808, polygonOffset: true, polygonOffsetFactor: -3, polygonOffsetUnits: -3 });
  const white = new THREE.MeshBasicMaterial({ color: 0xe0e0e0, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
  for (const dx of [-4.5, 4.5]) {
    const sq = new THREE.Mesh(new THREE.PlaneGeometry(1.1, 1.1), white);
    sq.rotation.x = -Math.PI / 2;
    sq.position.set(dx, 1.86, -0.4);
    g.add(sq);
    const cv = new THREE.Mesh(new THREE.PlaneGeometry(0.32, 1.0), black);
    cv.rotation.x = -Math.PI / 2;
    cv.position.set(dx, 1.87, -0.4);
    g.add(cv);
    const ch = new THREE.Mesh(new THREE.PlaneGeometry(1.0, 0.32), black);
    ch.rotation.x = -Math.PI / 2;
    ch.position.set(dx, 1.87, -0.4);
    g.add(ch);
  }

  return g;
}

/* ---------------------------------------------------------------------- *
 *  Ace — distinctive black/yellow checkered Sopwith Triplane mirror to
 *  the player's Fokker. A boss fight in plane form.
 * ---------------------------------------------------------------------- */
function makeAceMesh(): THREE.Group {
  const g = new THREE.Group();
  const black = new THREE.MeshStandardMaterial({ color: 0x111111, roughness: 0.7 });
  const yellow = new THREE.MeshStandardMaterial({ color: 0xd1a317, roughness: 0.65 });
  const metal = new THREE.MeshStandardMaterial({ color: 0x3a3a36, roughness: 0.4, metalness: 0.7 });
  const wing = new THREE.MeshStandardMaterial({ color: 0x161616, roughness: 0.78 });
  const wingTrim = new THREE.MeshStandardMaterial({ color: 0xb88a14, roughness: 0.85 });
  const trim = new THREE.MeshStandardMaterial({ color: 0x6a4a14, roughness: 0.85 });
  const leather = new THREE.MeshStandardMaterial({ color: 0x161616, roughness: 0.9 });
  const rubber = new THREE.MeshStandardMaterial({ color: 0x0a0a0a, roughness: 0.95 });

  // Fuselage — slab-sided but deeper than scout
  const body = new THREE.Mesh(new THREE.BoxGeometry(1.0, 1.15, 4.8, 6, 8, 18), black);
  body.position.set(0, 0, 0.4);
  g.add(body);
  // Yellow checker stripe down the side (instanced quads alternating colors)
  const checkSize = 0.28;
  for (let i = 0; i < 14; i++) {
    const z = -2.0 + i * checkSize;
    for (const sx of [-0.51, 0.51]) {
      const c = new THREE.Mesh(new THREE.PlaneGeometry(checkSize, 0.28),
        i % 2 === 0 ? yellow : black);
      c.position.set(sx, 0.05, z);
      c.rotation.y = sx > 0 ? Math.PI / 2 : -Math.PI / 2;
      g.add(c);
    }
  }
  // Nose cowl (rotary)
  const cowl = new THREE.Mesh(new THREE.CylinderGeometry(0.6, 0.6, 0.6, 56, 2), metal);
  cowl.rotation.x = Math.PI / 2;
  cowl.position.set(0, 0, -2.2);
  g.add(cowl);
  // Tail
  const tail = new THREE.Mesh(new THREE.ConeGeometry(0.3, 0.95, 32), black);
  tail.rotation.x = Math.PI / 2;
  tail.position.set(0, 0, 3.2);
  g.add(tail);

  // Three stacked wings (Sopwith Triplane shape)
  const wingSpec: [number, number, number][] = [
    [7.0, 1.9, -0.7],
    [6.4, 0.85, -0.3],
    [6.0, -0.55, 0.05],
  ];
  for (const [span, y, z] of wingSpec) {
    const w = makeAirfoilWing(span, 1.1, 0.18, wing, wing, wingTrim);
    w.position.set(0, y, z);
    g.add(w);
  }
  // Outboard struts between upper and middle, middle and lower
  for (const dx of [-2.7, 2.7]) {
    for (const yMid of [1.4, 0.15]) {
      const s = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 1.2, 20, 2), trim);
      s.position.set(dx, yMid, -0.5);
      g.add(s);
    }
  }

  // Pilot — yellow scarf to match livery
  const pit = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.32, 0.95, 6, 4, 8), new THREE.MeshStandardMaterial({ color: 0x080604, roughness: 1 }));
  pit.position.set(0, 0.6, 0.3);
  g.add(pit);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.2, 48, 32), leather);
  head.position.set(0, 0.85, 0.4);
  g.add(head);
  const goggles = new THREE.Mesh(new THREE.CylinderGeometry(0.21, 0.21, 0.06, 48, 2), new THREE.MeshStandardMaterial({ color: 0xd1a317, roughness: 0.3, metalness: 0.5 }));
  goggles.rotation.z = Math.PI / 2;
  goggles.position.set(0, 0.86, 0.4);
  g.add(goggles);

  // Prop with twisted blades + spinner
  const prop = new THREE.Group();
  const bladeG = makePropBladeGeom(2.4, 0.2);
  for (let i = 0; i < 2; i++) {
    const b = new THREE.Mesh(bladeG, trim);
    b.rotation.z = i === 0 ? 0 : Math.PI / 2;
    prop.add(b);
  }
  prop.add(new THREE.Mesh(new THREE.SphereGeometry(0.18, 48, 32), metal));
  const spinner = new THREE.Mesh(new THREE.ConeGeometry(0.2, 0.32, 40, 3), yellow);
  spinner.rotation.x = -Math.PI / 2;
  spinner.position.set(0, 0, -0.25);
  prop.add(spinner);
  prop.position.set(0, 0, -2.55);
  g.add(prop);
  g.userData.prop = prop;

  // Tail surfaces with checkered yellow rudder
  const hstab = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.12, 1.0, 24, 2, 6), wing);
  hstab.position.set(0, 0, 3.0);
  g.add(hstab);
  const fin = new THREE.Mesh(new THREE.BoxGeometry(0.14, 1.05, 1.0, 4, 10, 6), yellow);
  fin.position.set(0, 0.55, 3.1);
  g.add(fin);
  const rudder = new THREE.Mesh(new THREE.BoxGeometry(0.16, 1.05, 0.65, 4, 10, 4), black);
  rudder.position.set(0, 0.55, 3.7);
  g.add(rudder);

  // Twin Vickers
  for (const dx of [-0.18, 0.18]) {
    const gun = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.055, 1.0, 32, 2), metal);
    gun.rotation.x = Math.PI / 2;
    gun.position.set(dx, 0.7, -1.5);
    g.add(gun);
  }

  // Landing gear with yellow accents
  for (const dx of [-1.0, 1.0]) {
    const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.36, 0.36, 0.2, 56, 2), rubber);
    wheel.rotation.z = Math.PI / 2;
    wheel.position.set(dx, -1.15, -0.4);
    g.add(wheel);
    const hubC = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.22, 24, 2), yellow);
    hubC.rotation.z = Math.PI / 2;
    hubC.position.set(dx, -1.15, -0.4);
    g.add(hubC);
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.95, 24, 2), trim);
    leg.position.set(dx * 0.55, -0.75, -0.4);
    leg.rotation.z = dx > 0 ? 0.35 : -0.35;
    g.add(leg);
  }

  return g;
}
